import { combineBarcode, extractRawBarcode } from './barcodeUtils';
import { getProductVariants } from './productVariants';

const EDITABLE_PRODUCT_FIELDS = [
  'image', 'name', 'itemNumber', 'livePrice', 'onlinePrice', 'remarks', 'effects',
  'costKoreanWon', 'costJPY', 'costTWD', 'costTHB', 'costRMB', 'weight', 'costType',
  'vendor', 'styles', 'styleIds', 'styleBarcodes', 'rawStyleBarcodes', 'styleDetails',
  'photos', 'enableMultiImage', 'enableMultiPrice', 'enableMultiCostWeight',
];
const COST_FIELDS = { KRW: 'costKoreanWon', JPY: 'costJPY', THB: 'costTHB', RMB: 'costRMB', TWD: 'costTWD' };
const hasValue = value => value !== undefined && value !== null && value !== '';
const sameValue = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const productDataStatus = product => {
  const hasName = String(product.name ?? '').trim() !== '';
  const hasIdentity = [product.itemNumber, product.barcode, ...(product.rawStyleBarcodes || []), ...(product.styleBarcodes || [])]
    .some(value => String(value ?? '').trim() !== '');
  return hasName && hasIdentity ? 'ready' : 'pending';
};

// Normalize only what this form edits. The raw snapshot remains the concurrency base.
export const normalizeProductEditor = (data = {}, country) => {
  const defaultCostType = { korea: 'KRW', japan: 'JPY', china: 'RMB', thailand: 'THB' }[country] || 'TWD';
  const costType = data.costType || Object.keys(COST_FIELDS).find(type => hasValue(data[COST_FIELDS[type]])) || defaultCostType;
  const variants = getProductVariants(data);
  const styleDetails = variants.map(variant => {
    const original = data.styleDetails?.[variant.index];
    return original && typeof original === 'object' && !Array.isArray(original) ? { ...original } : {};
  });
  const form = Object.fromEntries([
    'image', 'name', 'itemNumber', 'livePrice', 'onlinePrice', 'remarks', 'effects',
    'costKoreanWon', 'costJPY', 'costTWD', 'costTHB', 'costRMB', 'weight', 'vendor',
  ].map(field => [field, data[field] ?? '']));
  return {
    ...form,
    costType,
    styles: variants.map(variant => variant.name),
    styleIds: variants.map(variant => variant.id),
    styleBarcodes: variants.map(variant => variant.barcode ?? ''),
    rawStyleBarcodes: variants.map(variant => variant.rawBarcode ?? extractRawBarcode(variant.barcode || '', data.itemNumber || '')),
    styleDetails,
    photos: Array.isArray(data.photos) ? data.photos.map(photo => typeof photo === 'object' && photo !== null ? { ...photo } : photo) : [],
    enableMultiImage: data.enableMultiImage ?? styleDetails.some(detail => hasValue(detail.image)),
    enableMultiPrice: data.enableMultiPrice ?? styleDetails.some(detail => hasValue(detail.livePrice) || hasValue(detail.onlinePrice)),
    enableMultiCostWeight: data.enableMultiCostWeight ?? styleDetails.some(detail => [...Object.values(COST_FIELDS), 'weight'].some(field => hasValue(detail[field]))),
    newStyle: '', newStyleBarcode: '', newStyleImage: '',
  };
};

export const buildProductEditorPatch = (base, original, form) => {
  const candidate = { ...form };
  if (form.itemNumber !== original.itemNumber || !sameValue(form.rawStyleBarcodes, original.rawStyleBarcodes)) {
    candidate.styleBarcodes = (form.rawStyleBarcodes || []).map(barcode => combineBarcode(barcode, form.itemNumber));
  }
  const patch = {};
  EDITABLE_PRODUCT_FIELDS.forEach(field => {
    if (!sameValue(candidate[field], original[field])) patch[field] = candidate[field];
  });
  // Persist deterministic legacy IDs once, without reshuffling or deleting slots.
  const variantsChanged = ['styles', 'styleBarcodes', 'rawStyleBarcodes', 'styleDetails'].some(field => field in patch);
  if (variantsChanged && !sameValue(base.styleIds, form.styleIds)) patch.styleIds = [...form.styleIds];
  if (base.dataStatus === 'pending' || base.dataStatus === 'ready') {
    const dataStatus = productDataStatus({ ...base, ...candidate });
    if (dataStatus !== base.dataStatus) patch.dataStatus = dataStatus;
  }
  return patch;
};

// Build from copies so a failed network save never appends variants to React state.
export const buildNewProduct = (form, pendingVariantId) => {
  const product = Object.fromEntries(EDITABLE_PRODUCT_FIELDS
    .filter(field => Object.prototype.hasOwnProperty.call(form, field))
    .map(field => [field, form[field]]));
  const variants = getProductVariants(form);
  const styles = variants.map(variant => variant.name);
  const styleIds = variants.map(variant => variant.id);
  const rawStyleBarcodes = variants.map(variant => variant.rawBarcode);
  const styleDetails = variants.map(variant => ({ ...(form.styleDetails?.[variant.index] || {}) }));
  const pendingBarcode = String(form.newStyleBarcode ?? '').trim();
  const pendingName = String(form.newStyle ?? '').trim();
  if (pendingBarcode || pendingName) {
    styles.push(pendingName || '無樣式');
    styleIds.push(pendingVariantId);
    rawStyleBarcodes.push(pendingBarcode);
    styleDetails.push({ active: true });
  }
  return {
    ...product,
    dataStatus: productDataStatus({ ...product, rawStyleBarcodes }),
    itemNumber: String(form.itemNumber ?? '').trim(),
    image: form.image ?? '',
    photos: (form.photos || []).map(photo => typeof photo === 'object' && photo !== null ? { ...photo } : photo),
    styles, styleIds, rawStyleBarcodes, styleDetails,
    styleBarcodes: rawStyleBarcodes.map(barcode => combineBarcode(barcode, String(form.itemNumber ?? '').trim())),
  };
};

// An inactive variant keeps its stable ID, barcode, and slot for existing references.
export const toggleEditorVariantActive = (form, styleId) => ({
  ...form,
  styleDetails: form.styleIds.map((id, index) => id === styleId
    ? { ...form.styleDetails[index], active: form.styleDetails[index]?.active === false }
    : form.styleDetails[index]),
});
